import React, { useState, useCallback } from 'react';
import { Loader2 } from 'lucide-react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import styles from './ResetPassword.module.css';
import Footer from '../../common/UI/Footer';
import { useNavigate, useParams } from 'react-router-dom';
import { useForgotPassword } from '../../hooks/useForgetpassword';

const ResetPassword = () => {
  const [passwords, setPasswords] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const { resetPassword, isLoading, error } = useForgotPassword();
  const navigate = useNavigate();
  const { token } = useParams();

  React.useEffect(() => {
    if (error) {
      const errorMessage = error?.response?.data?.message || 'An error occurred while resetting password';
      toast.error(errorMessage);
      setErrors(prev => ({ ...prev, submit: errorMessage }));
    }
  }, [error]);

  const validateForm = useCallback(() => {
    const newErrors = {};
    const { newPassword, confirmPassword } = passwords;

    if (!newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (newPassword.length < 8) {
      newErrors.newPassword = 'Password must be at least 8 characters long';
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (confirmPassword !== newPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    return newErrors;
  }, [passwords]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validateForm();

    if (Object.keys(newErrors).length === 0) {
      try {
        await resetPassword({ token, newPassword: passwords.newPassword }, {
          onSuccess: () => {
            toast.success('Password reset successful!');
            setPasswords({ newPassword: '', confirmPassword: '' });
            setTimeout(() => {
              navigate('/signin');
            }, 3000);
          }
        });
      } catch (err) {
        console.error('Error resetting password:', err);
      }
    } else {
      setErrors(newErrors);
      toast.error('Please fix the errors in the form');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setPasswords(prev => ({ ...prev, [name]: value }));
    if (errors[name] || errors.submit) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        delete newErrors.submit;
        return newErrors;
      });
    }
  };

  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />

      <div className={styles.container}>
        <div className={styles.formCard}>
          <h2>Reset Password</h2>
          <p className={styles.instructions}>
            Please enter your new password below.
          </p>
          <form onSubmit={handleSubmit} className={styles.form}>
            <div className={styles.inputGroup}>
              <input
                type="password"
                name="newPassword"
                placeholder="New Password"
                value={passwords.newPassword}
                onChange={handleChange}
                className={`${errors.newPassword ? styles.errorInput : ''}`}
                disabled={isLoading}
              />
              {errors.newPassword && <span className={styles.errorText}>{errors.newPassword}</span>}
            </div>

            <div className={styles.inputGroup}>
              <input
                type="password"
                name="confirmPassword"
                placeholder="Confirm Password"
                value={passwords.confirmPassword}
                onChange={handleChange}
                className={`${errors.confirmPassword ? styles.errorInput : ''}`}
                disabled={isLoading}
              />
              {errors.confirmPassword && <span className={styles.errorText}>{errors.confirmPassword}</span>}
            </div>

            {errors.submit && <span className={styles.errorText}>{errors.submit}</span>}

            <button
              type="submit"
              className={`${styles.submitButton} ${isLoading ? styles.loading : ''}`}
              disabled={isLoading}
            >
              {isLoading ? (
                <span className={styles.loadingWrapper}>
                  <Loader2 className={styles.spinner} size={20} />
                  Resetting...
                </span>
              ) : (
                'Reset Password'
              )}
            </button>

            <p className={styles.loginLink}>
              Remember your password?{' '}
              <span 
                className={styles.signInLink}
                onClick={() => navigate('/signin')}
              >
                Back to Sign in
              </span>
            </p>
          </form>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default ResetPassword;